import { AdminPageLayout } from '@/components/admin/AdminPageLayout';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { trpc } from '@/utils/trpc';
import { useSuperAdmin } from '@/utils/use-super-admin';
import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, useRouter } from '@tanstack/react-router';

import {
  AlertCircle,
  ArrowRight,
  CheckCircle,
  Download,
  ExternalLink,
  HelpCircle,
  RefreshCw,
  Upload,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

export const Route = createFileRoute('/_protected/admin/retell-agent-sync')({
  component: RetellAgentSyncPage,
});

const syncFormSchema = z.object({
  sourceAgentId: z.string().min(1, 'Source agent ID required'),
  targetAgentId: z.string().min(1, 'Target agent ID required'),
  sourceEnvironment: z.enum(['dev', 'prod']),
  targetEnvironment: z.enum(['dev', 'prod']),
});

type SyncFormData = z.infer<typeof syncFormSchema>;

function HelpDialog({ disabled }: { disabled: boolean }) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button type="button" variant="outline" size="lg" disabled={disabled}>
          <HelpCircle className="mr-2 h-4 w-4" />
          How to Use
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>How to Use Retell Agent Sync</DialogTitle>
          <DialogDescription>
            Follow these steps to safely sync agent configurations
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-3 text-sm">
            {[
              {
                num: 1,
                title: 'Select Source:',
                desc: 'Choose the environment and agent ID you want to copy FROM',
              },
              {
                num: 2,
                title: 'Select Target:',
                desc: 'Choose the environment and agent ID you want to copy TO',
              },
              {
                num: 3,
                title: 'Review & Confirm:',
                desc: "If syncing to production, you'll see a confirmation dialog",
              },
              {
                num: 4,
                title: 'Execute Sync:',
                desc: 'Click the button to start copying the configuration',
              },
            ].map((step) => (
              <div key={step.num} className="flex gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                  {step.num}
                </span>
                <p className="text-muted-foreground">
                  <span className="font-medium text-foreground">{step.title}</span> {step.desc}
                </p>
              </div>
            ))}
          </div>

          <div className="space-y-3">
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-900 dark:bg-blue-950">
              <p className="text-xs leading-relaxed text-blue-800 dark:text-blue-200">
                <strong>ℹ️ Knowledge Base Preservation:</strong> Target environment&apos;s knowledge
                base IDs are automatically preserved during sync to prevent breaking the target
                environment.
              </p>
            </div>

            <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3 dark:border-yellow-900 dark:bg-yellow-950">
              <p className="text-xs leading-relaxed text-yellow-800 dark:text-yellow-200">
                <strong>⚠️ Important:</strong> Only conversation flow agents are supported. The sync
                will copy all agent settings except the agent name and knowledge base
                configurations.
              </p>
            </div>

            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span>Need help finding agent IDs?</span>
              <a
                href="https://dashboard.retellai.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-primary hover:underline"
              >
                Visit Retell Dashboard
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SuccessResultCard({
  data,
}: {
  data: {
    targetAgentId?: string;
    targetConversationFlowId?: string;
    oldAgentVersion?: number;
    newAgentVersion?: number;
    oldCFVersion?: number;
    newCFVersion?: number;
  } | null;
}) {
  if (!data) return null;

  return (
    <Card className="border-green-200 bg-green-50/50 dark:border-green-900 dark:bg-green-950/20">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-green-900 dark:text-green-100">
          <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
          Sync Completed Successfully
        </CardTitle>
        <CardDescription>
          Agent configuration has been copied with knowledge base preservation
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <h4 className="text-sm font-semibold text-muted-foreground">Agent Update</h4>
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Target Agent ID:</span>
                <span className="font-mono text-xs">{data.targetAgentId}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Version:</span>
                <span className="font-mono text-xs">
                  v{data.oldAgentVersion} → v{data.newAgentVersion}
                </span>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <h4 className="text-sm font-semibold text-muted-foreground">
              Conversation Flow Update
            </h4>
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Flow ID:</span>
                <span className="font-mono text-xs">{data.targetConversationFlowId}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Version:</span>
                <span className="font-mono text-xs">
                  v{data.oldCFVersion} → v{data.newCFVersion}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-green-200 bg-white/50 p-3 dark:border-green-900 dark:bg-green-950/30">
          <p className="text-xs text-green-800 dark:text-green-200">
            <strong>✓ Knowledge bases preserved:</strong> Target environment&apos;s knowledge base
            configurations were maintained during the sync.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

function ErrorResultCard({ error }: { error: { message: string } }) {
  return (
    <Card className="border-red-200 bg-red-50/50 dark:border-red-900 dark:bg-red-950/20">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-red-900 dark:text-red-100">
          <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
          Sync Failed
        </CardTitle>
        <CardDescription>An error occurred while copying the agent configuration</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="rounded-lg border border-red-200 bg-white/50 p-3 dark:border-red-900 dark:bg-green-950/30">
          <p className="text-sm text-red-800 dark:text-red-200">{error.message}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function ConfirmationDialog({
  open,
  pendingSync,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  pendingSync: SyncFormData | null;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <AlertDialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-orange-900 dark:text-orange-100">
            <AlertCircle className="h-5 w-5 text-orange-600 dark:text-orange-400" />
            Confirm Production Sync
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-4">
            <p>
              You are about to copy agent configuration <strong>to production</strong>. This will
              modify the production agent and may affect live operations.
            </p>

            {pendingSync && (
              <div className="rounded-lg border border-orange-200 bg-orange-50/50 p-3 dark:border-orange-900 dark:bg-orange-950/30">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Source:</span>
                    <span className="font-mono text-xs">
                      {pendingSync.sourceEnvironment} / {pendingSync.sourceAgentId}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Target:</span>
                    <span className="font-mono text-xs font-semibold text-orange-700 dark:text-orange-300">
                      prod / {pendingSync.targetAgentId}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <p className="text-sm">
              <strong>Note:</strong> Knowledge base configurations will be preserved in the target
              environment.
            </p>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            className="bg-orange-600 hover:bg-orange-700 dark:bg-orange-600 dark:hover:bg-orange-700"
          >
            Confirm Sync to Production
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function SyncForm({
  form,
  syncMutation,
  onSubmit,
}: {
  form: UseFormReturn<SyncFormData>;
  syncMutation: ReturnType<typeof trpc.admin.syncAgent.useMutation>;
  onSubmit: (data: SyncFormData) => void;
}) {
  const isDisabled = syncMutation.isPending;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] gap-6">
          {/* Source Configuration */}
          <div className="space-y-4 p-4 rounded-lg border-2 border-blue-200 bg-blue-50/50 dark:border-blue-900 dark:bg-blue-950/20">
            <div className="flex items-center gap-2 pb-2 border-b border-blue-200 dark:border-blue-900">
              <Download className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100 uppercase">
                Source
              </h3>
              <Badge
                variant="outline"
                className="ml-auto text-blue-700 border-blue-300 dark:text-blue-300 dark:border-blue-700"
              >
                Copy From
              </Badge>
            </div>

            <FormField
              control={form.control}
              name="sourceEnvironment"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Environment</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    disabled={isDisabled}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select environment" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="dev">Development</SelectItem>
                      <SelectItem value="prod">Production</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormDescription>Where to copy FROM</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="sourceAgentId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Agent ID</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="agent_xxx"
                      {...field}
                      disabled={isDisabled}
                      className="font-mono text-sm"
                    />
                  </FormControl>
                  <FormDescription>Retell agent ID to copy from</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Arrow Separator */}
          <div className="hidden lg:flex items-center justify-center px-4">
            <ArrowRight className="h-8 w-8 text-muted-foreground" />
          </div>

          {/* Target Configuration */}
          <div className="space-y-4 p-4 rounded-lg border-2 border-green-200 bg-green-50/50 dark:border-green-900 dark:bg-green-950/20">
            <div className="flex items-center gap-2 pb-2 border-b border-green-200 dark:border-green-900">
              <Upload className="h-4 w-4 text-green-600 dark:text-green-400" />
              <h3 className="text-sm font-semibold text-green-900 dark:text-green-100 uppercase">
                Target
              </h3>
              <Badge
                variant="outline"
                className="ml-auto text-green-700 border-green-300 dark:text-green-300 dark:border-green-700"
              >
                Copy To
              </Badge>
            </div>

            <FormField
              control={form.control}
              name="targetEnvironment"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Environment</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    disabled={isDisabled}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select environment" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="dev">Development</SelectItem>
                      <SelectItem value="prod">Production</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormDescription>Where to copy TO</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="targetAgentId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Agent ID</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="agent_yyy"
                      {...field}
                      disabled={isDisabled}
                      className="font-mono text-sm"
                    />
                  </FormControl>
                  <FormDescription>Retell agent ID to copy to</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        {/* Submit Button and Help */}
        <div className="flex justify-between items-center pt-4">
          <HelpDialog disabled={isDisabled} />

          <Button type="submit" size="lg" disabled={isDisabled}>
            <RefreshCw className={`mr-2 h-4 w-4 ${isDisabled ? 'animate-spin' : ''}`} />
            {isDisabled ? 'Copying...' : 'Copy Configuration'}
          </Button>
        </div>
      </form>
    </Form>
  );
}

function RetellAgentSyncPage() {
  const { isSuperAdmin, isLoading: isAdminLoading } = useSuperAdmin();
  const router = useRouter();

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingSync, setPendingSync] = useState<SyncFormData | null>(null);

  const form = useForm<SyncFormData>({
    resolver: zodResolver(syncFormSchema),
    defaultValues: {
      sourceAgentId: '',
      targetAgentId: '',
      sourceEnvironment: 'dev',
      targetEnvironment: 'prod',
    },
  });

  const syncMutation = trpc.admin.syncAgent.useMutation({
    onSuccess: (data) => {
      toast.success('Agent configuration copied successfully', {
        description: `Agent updated from v${data.oldAgentVersion} to v${data.newAgentVersion}`,
      });
      form.reset();
    },
    onError: (error) => {
      toast.error('Failed to copy agent configuration', {
        description: error.message,
      });
    },
  });

  useEffect(() => {
    if (!isAdminLoading && !isSuperAdmin) {
      router.navigate({ to: '/dashboard' });
    }
  }, [isAdminLoading, isSuperAdmin, router]);

  const onSubmit = (data: SyncFormData) => {
    if (data.targetEnvironment === 'prod') {
      setPendingSync(data);
      setShowConfirmDialog(true);
    } else {
      syncMutation.mutate(data);
    }
  };

  const handleConfirmSync = () => {
    if (pendingSync) {
      syncMutation.mutate(pendingSync);
      setShowConfirmDialog(false);
      setPendingSync(null);
    }
  };

  const handleCancelSync = () => {
    setShowConfirmDialog(false);
    setPendingSync(null);
  };

  if (isAdminLoading || !isSuperAdmin) {
    return null;
  }

  return (
    <AdminPageLayout
      title="Retell Agent Sync"
      description="Copy agent configurations between dev and prod environments"
      icon={RefreshCw}
      isSuperAdminPage={true}
    >
      <div className="space-y-6">
        <Card className={syncMutation.isPending ? 'opacity-75' : ''}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Configuration Sync
              {syncMutation.isPending && (
                <Badge variant="secondary" className="ml-2">
                  <RefreshCw className="mr-1 h-3 w-3 animate-spin" />
                  Syncing...
                </Badge>
              )}
            </CardTitle>
            <CardDescription>
              Synchronize Retell agent configurations across environments with knowledge base
              preservation
            </CardDescription>
          </CardHeader>
          <CardContent>
            <SyncForm form={form} syncMutation={syncMutation} onSubmit={onSubmit} />
          </CardContent>
        </Card>

        {syncMutation.isSuccess && <SuccessResultCard data={syncMutation.data} />}
        {syncMutation.isError && <ErrorResultCard error={syncMutation.error} />}

        <ConfirmationDialog
          open={showConfirmDialog}
          pendingSync={pendingSync}
          onConfirm={handleConfirmSync}
          onCancel={handleCancelSync}
        />
      </div>
    </AdminPageLayout>
  );
}
